import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Sparkles, UserPlus, Disc3, TrendingUp, Link2 } from "lucide-react";
import api, { thumbUrl } from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { PlatformBadge } from "@/components/PlatformBadge";
import { TrackSoundDialog } from "@/components/dialogs/TrackSoundDialog";
import { AddArtistDialog } from "@/components/dialogs/AddArtistDialog";
import { ImportLinkDialog } from "@/components/dialogs/ImportLinkDialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtShort, fmtFull } from "@/lib/format";

export default function Artists() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [artists, setArtists] = useState(null);
  const [track, setTrack] = useState(false);
  const [addArtist, setAddArtist] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const load = () => api.get("/artists").then((r) => setArtists(r.data)).catch(() => toast.error("Failed to load artists"));
  useEffect(() => { load(); }, []);

  // Mobile menu routes here with ?import=1 / ?track=1 to open the matching dialog.
  useEffect(() => {
    if (searchParams.get("import") === "1") {
      setImportOpen(true);
      searchParams.delete("import");
      setSearchParams(searchParams, { replace: true });
    } else if (searchParams.get("track") === "1") {
      setTrack(true);
      searchParams.delete("track");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const totalViews = (artists || []).reduce((a, x) => a + (x.total_views || 0), 0);
  const totalPosts = (artists || []).reduce((a, x) => a + x.total_posts, 0);

  return (
    <div className="min-h-screen">
      <TopBar
        right={
          <>
            <Button data-testid="import-link-btn" onClick={() => setImportOpen(true)} variant="outline" className="rounded-full border-[#35606E]/30 bg-white text-[#35606E] hover:bg-[#35606E]/5">
              <Link2 className="mr-1.5 h-4 w-4" /> Import link
            </Button>
            <Button data-testid="track-sound-btn" onClick={() => setTrack(true)} className="rounded-full bg-[#35606E] hover:bg-[#294b57]">
              <Plus className="mr-1.5 h-4 w-4" /> Track a Sound
            </Button>
          </>
        }
      />

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-10 lg:px-8">
        <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
          <div>
            <p className="flex items-center gap-2 text-sm font-medium text-neutral-500">
              <Sparkles className="h-4 w-4" /> Your roster
            </p>
            <h1 className="mt-1 font-display text-4xl font-extrabold tracking-tight sm:text-5xl">Artists</h1>
            <p className="mt-2 max-w-lg text-neutral-500">Every artist and the sound campaigns you're tracking across Reels and TikTok.</p>
          </div>
          <div className="flex gap-6">
            <div>
              <p className="num-display text-3xl font-extrabold">{fmtShort(totalViews)}</p>
              <p className="text-xs text-neutral-500">Total views</p>
            </div>
            <div>
              <p className="num-display text-3xl font-extrabold">{totalPosts}</p>
              <p className="text-xs text-neutral-500">Live posts</p>
            </div>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {artists === null &&
            Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-72 rounded-2xl" />)}

          {artists?.map((a, i) => (
            <motion.button
              key={a.id}
              data-testid={`artist-card-${a.id}`}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -6 }}
              onClick={() => navigate(`/artists/${a.id}`)}
              className="pb-card group overflow-hidden p-0 text-left"
            >
              <div className="relative aspect-[16/10] overflow-hidden bg-neutral-900">
                {a.image ? (
                  <img src={thumbUrl(a.image, 320)} alt={a.name} loading="lazy" decoding="async" className="h-full w-full object-cover opacity-90 transition-transform duration-500 group-hover:scale-105" />
                ) : (
                  <div className="grid h-full place-items-center text-neutral-600"><Disc3 className="h-12 w-12" /></div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between">
                  <h3 className="font-display text-xl font-bold text-white">{a.name}</h3>
                  <div className="flex gap-1.5">
                    {a.platforms.map((p) => <PlatformBadge key={p} platform={p} />)}
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between px-5 py-4">
                <span className="text-sm text-neutral-500">{a.campaign_count} campaign{a.campaign_count === 1 ? "" : "s"}</span>
                <span className="flex items-center gap-1.5 text-sm font-semibold text-[#111]">
                  <TrendingUp className="h-4 w-4 text-neutral-400" /> {a.total_views == null ? "—" : fmtFull(a.total_views)} views
                </span>
              </div>
            </motion.button>
          ))}

          {artists && (
            <button
              data-testid="add-artist-card"
              onClick={() => setAddArtist(true)}
              className="group flex min-h-[18rem] flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-neutral-300 text-neutral-400 transition-colors hover:border-neutral-900 hover:text-neutral-900"
            >
              <span className="grid h-14 w-14 place-items-center rounded-full bg-neutral-100 transition-colors group-hover:bg-neutral-900 group-hover:text-white">
                <UserPlus className="h-6 w-6" />
              </span>
              <span className="font-medium">Add an artist</span>
            </button>
          )}
        </div>
      </main>

      <TrackSoundDialog open={track} onOpenChange={setTrack} onCreated={(c) => navigate(`/songs/${c.song_id}`)} />
      <AddArtistDialog open={addArtist} onOpenChange={setAddArtist} onCreated={load} />
      <ImportLinkDialog open={importOpen} onOpenChange={setImportOpen} onImported={(res) => navigate(`/songs/${res.song_id}`)} />
    </div>
  );
}
